import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatDateToISO } from '../utils';

/**
 * 日付選択とURLパラメータ同期を管理するカスタムフック
 * 患者IDごとに日付を保持し、利用者切り替え時に復元します
 *
 * @param latestDate - 最新の連絡帳日付（初期値として使用）
 * @param patientId - 患者ID（利用者切り替え検知用）
 * @returns 日付選択の状態と操作関数
 */
export const useDateNavigation = (latestDate?: string | null, patientId?: number | null) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // 患者IDごとの日付を保存するMap
  const [patientDates, setPatientDates] = useState<Map<number, Date>>(new Map());

  // ユーザーが手動で日付を変更したかどうかを追跡
  const [hasUserChangedDate, setHasUserChangedDate] = useState<boolean>(false);

  // 前回の患者IDを追跡
  const prevPatientIdRef = useRef<number | null | undefined>(null);

  // 初回ロード判定（患者切り替えロジックが初期化時に発火するのを防ぐ）
  const isInitialLoadRef = useRef<boolean>(true);

  // 初回マウント時のURL日付パラメータを保存（latestDateによる上書きを防ぐため）
  const urlDateRef = useRef<string | null>(searchParams.get('date'));

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      const parsedDate = new Date(dateParam);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate;
      }
    }
    // URLパラメータがない場合はundefinedで初期化（latestDate確定まで待機）
    return undefined;
  });

  // 患者IDが変わった場合の処理
  useEffect(() => {
    const prevPatientId = prevPatientIdRef.current;

    if (patientId !== null && patientId !== undefined) {
      // 初回ロード時は患者切り替えロジックをスキップ
      // （URL日付パラメータの削除を防ぐため）
      if (isInitialLoadRef.current) {
        prevPatientIdRef.current = patientId;
        isInitialLoadRef.current = false;
        return;
      }

      // 前回の患者IDと異なる場合（利用者切り替え）
      if (prevPatientId !== null && prevPatientId !== undefined && prevPatientId !== patientId) {
        // 1. 前の患者の現在日付を保存（selectedDateがある場合のみ）
        if (selectedDate) {
          setPatientDates(prev => {
            const newMap = new Map(prev);
            newMap.set(prevPatientId, selectedDate);
            return newMap;
          });
        }

        // 2. 新しい患者の保存済み日付を復元
        const savedDate = patientDates.get(patientId);

        // 3. 患者切り替え時はユーザー変更フラグをリセット（新しい患者では最新日付自動設定を有効化）
        setHasUserChangedDate(false);

        if (savedDate) {
          // 保存された日付がある場合は復元
          setSelectedDate(savedDate);
          setSearchParams(prev => {
            const params = new URLSearchParams(prev);
            params.set('date', formatDateToISO(savedDate));
            return params;
          }, { replace: true });
        } else {
          // 初めての患者：undefinedにリセットしてlatestDateを待つ
          urlDateRef.current = null; // URL日付参照をクリア（latestDateによる自動設定を有効化）
          setSelectedDate(undefined);
          setSearchParams(prev => {
            const params = new URLSearchParams(prev);
            params.delete('date');
            return params;
          }, { replace: true });
        }
      }
    }

    // 前回の患者IDを更新
    prevPatientIdRef.current = patientId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  // 最新日付が取得できた場合に自動設定
  useEffect(() => {
    // URL日付パラメータまたはユーザーが手動で日付を変更した場合は、latestDateによる上書きを防ぐ
    if (urlDateRef.current || hasUserChangedDate) {
      return; // 保護：URL日付またはユーザー選択日付を尊重
    }

    // URLパラメータがなく、ユーザーも日付を変更していない場合のみ、最新日付を自動設定
    // latestDateがundefinedの場合は何もしない（データ取得中）
    if (latestDate === undefined) return;

    if (latestDate) {
      // 最新日付がある場合
      const latestDateObj = new Date(latestDate);
      setSelectedDate(latestDateObj);
      setSearchParams(prev => {
        const params = new URLSearchParams(prev);
        params.set('date', formatDateToISO(latestDateObj));
        return params;
      }, { replace: true });

      // 患者IDごとのMapにも保存
      if (patientId !== null && patientId !== undefined) {
        setPatientDates(prev => {
          const newMap = new Map(prev);
          newMap.set(patientId, latestDateObj);
          return newMap;
        });
      }
    } else {
      // 最新日付がnullの場合（データがない）は今日を設定
      const today = new Date();
      setSelectedDate(today);
      setSearchParams(prev => {
        const params = new URLSearchParams(prev);
        params.set('date', formatDateToISO(today));
        return params;
      }, { replace: true });

      // 患者IDごとのMapにも保存
      if (patientId !== null && patientId !== undefined) {
        setPatientDates(prev => {
          const newMap = new Map(prev);
          newMap.set(patientId, today);
          return newMap;
        });
      }
    }
  }, [latestDate, setSearchParams, patientId, setPatientDates, hasUserChangedDate]);

  /**
   * 日付を変更してURLパラメータも更新
   * 患者IDごとのMapにも保存
   */
  const changeDate = (newDate: Date) => {
    // ユーザーが手動で日付を変更したことをマーク
    setHasUserChangedDate(true);

    setSelectedDate(newDate);
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('date', formatDateToISO(newDate));
      return params;
    });

    // 現在の患者IDの日付をMapに保存
    if (patientId !== null && patientId !== undefined) {
      setPatientDates(prev => {
        const newMap = new Map(prev);
        newMap.set(patientId, newDate);
        return newMap;
      });
    }
  };

  /**
   * 今日の日付に移動
   */
  const goToToday = () => {
    const today = new Date();
    changeDate(today);
  };

  /**
   * 前の日に移動
   */
  const goToPrevDay = () => {
    if (!selectedDate) return;
    const prevDay = new Date(selectedDate);
    prevDay.setDate(prevDay.getDate() - 1);
    changeDate(prevDay);
  };

  /**
   * 次の日に移動
   */
  const goToNextDay = () => {
    if (!selectedDate) return;
    const nextDay = new Date(selectedDate);
    nextDay.setDate(nextDay.getDate() + 1);
    changeDate(nextDay);
  };

  return {
    selectedDate,
    selectedDateString: selectedDate ? formatDateToISO(selectedDate) : undefined,
    changeDate,
    goToToday,
    goToPrevDay,
    goToNextDay,
  };
};
